import { Controller } from "@hotwired/stimulus"

// Live activity feed: the list starts full, and every interval a new row is
// cloned from the <template> and prepended at the top with a quick fade-in,
// pushing the rest down; the oldest row drops off the bottom. Loops forever,
// pauses on hidden tabs, and renders static for reduced motion.
export default class extends Controller {
  static targets = ["source", "list"]

  connect() {
    this.rows = Array.from(this.sourceTarget.content.children)
    if (this.rows.length === 0) return

    this.lastIndex = -1
    this.fillStatic()

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.inView = true
    this.onVisibility = () => this.updatePlayState()
    this.onResize = () => this.fillStatic()
    document.addEventListener("visibilitychange", this.onVisibility)
    window.addEventListener("resize", this.onResize)

    // Only stream while on screen and the tab is visible.
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting
      this.updatePlayState()
    })
    this.observer.observe(this.element)
    this.play()
  }

  disconnect() {
    this.pause()
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility)
    if (this.onResize) window.removeEventListener("resize", this.onResize)
    if (this.observer) this.observer.disconnect()
  }

  updatePlayState() {
    this.inView && !document.hidden ? this.play() : this.pause()
  }

  cloneRow(i) {
    return this.rows[i % this.rows.length].cloneNode(true)
  }

  // A random row, avoiding an immediate repeat.
  randomRow() {
    let i
    do {
      i = Math.floor(Math.random() * this.rows.length)
    } while (this.rows.length > 1 && i === this.lastIndex)
    this.lastIndex = i
    return this.rows[i].cloneNode(true)
  }

  // Fill until the rows overflow the panel (plus a buffer), so the bottom is
  // always covered. Keys off the panel's own fixed height — not the live
  // viewport — so mobile URL-bar resize events don't trigger refills.
  fillStatic() {
    const target = (this.element.clientHeight || window.innerHeight * 0.5) + 96
    let guard = 0
    while (this.listTarget.scrollHeight < target && guard < 300) {
      this.listTarget.appendChild(this.randomRow())
      guard += 1
    }
    this.capacity = this.listTarget.children.length
  }

  play() {
    if (this.timer) return
    if (this.burstLeft == null) this.burstLeft = this.randomBurst()
    this.timer = setTimeout(() => this.tick(), 500)
  }

  pause() {
    clearTimeout(this.timer)
    this.timer = null
  }

  randomBurst() {
    return 1 + Math.floor(Math.random() * 4) // 1–4 rows per cluster
  }

  between(min, max) {
    return min + Math.random() * (max - min)
  }

  tick() {
    this.addRow()
    this.burstLeft -= 1

    let delay
    if (this.burstLeft > 0) {
      delay = this.between(180, 360) // quick — within the cluster
    } else {
      this.burstLeft = this.randomBurst()
      delay = this.between(1100, 2400) // longer — between clusters
    }
    this.timer = setTimeout(() => this.tick(), delay)
  }

  addRow() {
    const row = this.randomRow()
    this.listTarget.prepend(row)
    if (this.listTarget.children.length > this.capacity) {
      this.listTarget.lastElementChild.remove()
    }

    row.animate(
      [
        { opacity: 0, transform: "translateY(-6px)" },
        { opacity: 1, transform: "translateY(0)" }
      ],
      { duration: 280, easing: "ease-out" }
    )
    row.animate(
      [
        { backgroundColor: "rgba(34, 197, 94, 0.45)" },
        { backgroundColor: "rgba(0, 0, 0, 0)" }
      ],
      { duration: 1800, easing: "ease-out" }
    )
  }
}
